import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  OnInit,
  Output,
  Renderer2,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { combineLatest } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

import { GasInputBoolean } from '@cikrf/gas-utils/decorators';

import { GasCheckbox } from '../gas-checkbox.namespace';
import { GasForm } from '../../gas-form.namespace';
import { GasFormBaseComponent } from '../../gas-form-base-component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'gas-checkbox-group',
  templateUrl: './gas-checkbox-group.component.html',
  styleUrls: ['./gas-checkbox-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GasCheckboxGroupComponent),
      multi: true,
    },
  ],
})
export class GasCheckboxGroupComponent extends GasFormBaseComponent implements ControlValueAccessor, OnInit {
  @Input()
  public title: string;

  @Input()
  public checkboxes: GasCheckbox.Checkbox[] = [];

  @HostBinding('class._expandable')
  @GasInputBoolean()
  @Input()
  public expandable: string | boolean;

  @GasInputBoolean()
  @Input()
  public visibleChanges: string | boolean;

  @GasInputBoolean()
  @Input()
  public multi: string | boolean;

  @Output()
  public clicked = new EventEmitter();

  public expanded = false;

  public form = new FormControl();

  public checkboxControls: { key: string; control: FormControl }[] = [];

  constructor(
    public changeDetectorRef: ChangeDetectorRef,
    public elementRef: ElementRef,
    public renderer: Renderer2,
  ) {
    super(changeDetectorRef, elementRef, renderer);
  }

  public ngOnInit(): void {
    this.checkboxes.forEach(item => {
      const control = new FormControl(item.checked);
      this.checkboxControls.push({ key: item.key, control });
    });

    this.form.valueChanges.pipe(
      untilDestroyed(this),
    ).subscribe((values: Record<string, boolean>[]) => {
      if (!values) {
        return;
      }

      values.forEach((innerValue: Record<string, boolean>) => {
        this.toggleCheckbox(Object.keys(innerValue)[0]);
      });
    });

    const subscriptions = this.checkboxControls.map(item => item.control.valueChanges.pipe(
      startWith(item.control.value),
      map(value => {
        const obj = new Object();

        obj[item.key] = value;

        return obj;
      }),
    ));

    combineLatest(subscriptions)
      .pipe(
        untilDestroyed(this),
      )
      .subscribe((result: Record<string, unknown>[]) => {
        this.onChange(result);
      });
  }

  public getExpanded(): boolean {
    if (!this.expandable || this.expanded) {
      return true;
    }
    return false;
  }

  public toggleCheckbox(key: string): void {
    this.checkboxControls.find(item => {
      if (item.key === key) {
        item.control.markAsDirty();
        item.control.setValue(!item.control.value);
      }
    });

    this.clicked.emit(key);
  }

  public getCheckboxValue(key: string): boolean {
    return this.checkboxControls.find(item => item.key === key).control.value;
  }

  public getCheckboxState(item: GasCheckbox.Checkbox): GasForm.InputState {
    if (item.type === GasForm.InputState.Default && this.getCheckboxModify(item)) {
      return GasForm.InputState.Primary;
    }
    return item.type;
  }

  public getCheckboxModify(item: GasCheckbox.Checkbox): boolean {
    return this.checkboxControls.find(control => control.key === item.key)?.control?.dirty;
  }

  public toggleExpand(): void {
    this.expanded = !this.expanded;
  }
}
